import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';

/**
 * Unified capacity-limit model across all tiers (number/provider/org/campaign).
 * Backend: teeoff-cpass-backend /telephony/capacity-limits. cpass is the system
 * of record; rows propagate to the voiceflow runtime for local enforcement.
 */
export type LimitScope = 'NUMBER' | 'PROVIDER' | 'ORG' | 'CAMPAIGN';

export interface CapacityLimit {
  id?: number;
  scope: LimitScope;
  scope_value: string;
  cps?: number | null;
  max_channels?: number | null;
  source?: 'MANUAL' | 'DERIVED';
  enabled?: boolean;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  /** Why this change is made. Sent, never returned: written to the capacity history. Required. */
  reason?: string;
}

export type ChangeDirection = 'INITIAL' | 'SET' | 'INCREASE' | 'DECREASE' | 'REMOVE';

/** One entry of a scope's capacity history (newest first). */
export interface CapacityHistoryEntry {
  id: number;
  scope: LimitScope;
  scope_value: string;
  limit_id?: number | null;
  direction: ChangeDirection;
  old_cps: number | null;
  new_cps: number | null;
  old_max_channels: number | null;
  new_max_channels: number | null;
  old_enabled?: boolean | number | null;
  new_enabled?: boolean | number | null;
  reason: string;
  changed_by: string;
  change_path: string;
  changed_at: string;
}

export type EnforcementStatus = 'ENFORCED' | 'PARTIAL' | 'NOT_ON_SWITCH' | 'MISMATCH' | 'NOT_ENFORCEABLE' | 'UNVERIFIED';

export interface SwitchDimension { type: string; value: string }

export interface SwitchCheck {
  switchProviderId: number | null;
  switchName: string | null;
  dimensions: SwitchDimension[];
  status: EnforcementStatus;
  reason: string | null;
  switchCps: number | null;
  switchConcurrency: number | null;
  matchedRows: string[];
}

export interface LimitCheck {
  limitId: number;
  scope: LimitScope;
  scopeValue: string;
  source?: string;
  cps: number | null;
  maxChannels: number | null;
  enabled: boolean;
  status: EnforcementStatus;
  reason: string | null;
  switches: SwitchCheck[];
  caveats: string[];
  placeholder: boolean;
}

/** One live counter the switch reports (voiceflow capacity patch, 2026-10-14). */
export interface SwitchPeak {
  dimensionType: string;
  dimensionValue: string;
  direction: 'outbound' | 'inbound';
  current: number;
  peak: number;
  peakAt: string | null;
}

/**
 * What the switch itself says about enforcement (read from its api-gateway). Null when
 * cpass did not read it (no control_url / admin token). error "patch not applied" when the
 * switch has no enforcement endpoint (404).
 */
export interface SwitchRuntime {
  reachable: boolean;
  error: string | null;
  mode: 'shadow' | 'enforce' | null;
  concurrencySafeReported: boolean | null;
  patch: string | null;
  reconcilerLastRunAt: string | null;
  asteriskChannels: number | null;
  wouldRefuseTotal: number | null;
  refusedTotal: number | null;
  peakSince: string | null;
  peaks: SwitchPeak[];
}

export interface SwitchSummary {
  providerId: number; name: string; sourceKind: string | null; readable: boolean; error: string | null;
  dbRows: number; labels: number; numbers: number;
  /** cpass config capacity.switch.<id>.concurrency-safe: false = only CPS may be pushed there. */
  concurrencySafe?: boolean;
  runtime?: SwitchRuntime | null;
}

export type PushResultStatus = 'VERIFIED' | 'FAILED' | 'NO_TARGET' | 'NOT_TRANSLATABLE' | 'CPASS_ONLY' | 'SKIPPED' | 'UNVERIFIABLE';

export interface PushTarget {
  switchProviderId: number | null;
  switchName: string | null;
  kind: string;
  action: 'PUSH' | 'SKIP';
  reason: string | null;
  dimensions: SwitchDimension[];
  cps: number | null;
  concurrency: number | null;
  concurrencyWithheldReason: string | null;
}

export interface PushPreview {
  limitId: number;
  scope: LimitScope;
  scopeValue: string;
  syncEnabled: boolean;
  targets: PushTarget[];
}

export interface PushResult {
  switchProviderId: number | null;
  switchName: string | null;
  status: PushResultStatus;
  error: string | null;
  dimensions: SwitchDimension[];
}

/** One capacity_limit_sync row (raw DB columns, snake_case). */
export interface LimitSyncRow {
  switch_key?: string;
  switch_name: string | null;
  dimension_key: string;
  check_status?: string | null;
  check_reason?: string | null;
  push_status?: string | null;
  push_error?: string | null;
  last_pushed_at?: string | null;
  last_verified_at?: string | null;
  last_checked_at?: string | null;
}

export interface PushOutcome {
  limitId: number;
  results: PushResult[];
  sync: LimitSyncRow[];
}

export interface UnmanagedRow {
  switchProviderId: number; switchName: string; type: string; value: string; cps: number | null;
  concurrency: number | null; enabled: boolean; effective: boolean; origin: 'DB' | 'SEED'; notes: string | null;
}

export interface LookupRow {
  did: string; organizationId: string; vendor: string; enabledInCpass: boolean; onSwitches: string[];
  status: 'ENABLED_UNCONFIRMED' | 'NOT_ON_SWITCH' | 'UNVERIFIED' | 'DISABLED'; note: string;
}

export interface EnforcementPanel {
  generatedAt: string;
  pushEnabled: boolean;
  /** cpass NUMBER / CAMPAIGN limits on cpass-placed calls: false = shadow ("would refuse" logged only). */
  cpassEnforceLimits?: boolean;
  switches: SwitchSummary[];
  counts: Record<string, number>;
  limits: LimitCheck[];
  unmanaged: UnmanagedRow[];
  lookup: LookupRow[];
  notes: string[];
}

export interface TrunkCapacityChange {
  cps?: number | null;
  maxChannels?: number | null;
  clearCps?: boolean;
  clearMaxChannels?: boolean;
  reason: string;
}

export interface ResolveResult {
  applied: CapacityLimit[];
  effectiveCps: number | null;
  effectiveChannels: number | null;
}

// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const capacityLimitsService = {
  list: async (scope?: LimitScope): Promise<ApiResponse<CapacityLimit[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.CAPACITY_LIMITS, { params: scope ? { scope } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to fetch limits'); }
  },
  upsert: async (data: CapacityLimit): Promise<ApiResponse<CapacityLimit>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.CAPACITY_LIMITS, data)).data; }
    catch (e: any) { return err(e, 'Failed to save limit'); }
  },
  /** Delete a limit. The reason is required and recorded in the capacity history. */
  remove: async (id: number, reason: string): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.CAPACITY_LIMIT_DETAIL(id), { params: { reason } })).data; }
    catch (e: any) { return err(e, 'Failed to delete limit'); }
  },
  /**
   * What this organization's trunk holds, and what is already given out.
   *
   * The trunk is the one its OUTBOUND route uses — not whichever provider owns
   * its numbers, which for a business with numbers on several carriers is a
   * different and wrong answer.
   */
  allocation: async (businessId: string) => {
    try { return (await axiosInstance.get(API_ENDPOINTS.CAPACITY_ALLOCATION, { params: { businessId } })).data; }
    catch (e: any) { return err(e, 'Failed to load allocation'); }
  },

  /** Who changed this scope's capacity, when, old → new, and why (newest first). */
  history: async (scope: LimitScope, value: string): Promise<ApiResponse<CapacityHistoryEntry[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.CAPACITY_LIMITS_HISTORY, { params: { scope, value } })).data; }
    catch (e: any) { return err(e, 'Failed to load history'); }
  },

  /** Master / Green Giraffe: is each limit actually enforced on the switch? Reads switches only. */
  enforcement: async (): Promise<ApiResponse<EnforcementPanel>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.CAPACITY_ENFORCEMENT)).data; }
    catch (e: any) { return err(e, 'Failed to check the switches'); }
  },

  /** Stores the check's verdicts (capacity_limit_sync). */
  recordEnforcement: async (): Promise<ApiResponse<{ recorded: number; counts: Record<string, number> }>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.CAPACITY_ENFORCEMENT_RECORD)).data; }
    catch (e: any) { return err(e, 'Failed to record the check'); }
  },

  /** Increase / decrease / clear a trunk's channels or CPS (master). Recorded; not pushed. */
  changeTrunk: async (providerId: number, body: TrunkCapacityChange): Promise<ApiResponse<any>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.CAPACITY_TRUNK_CHANGE(providerId), body)).data; }
    catch (e: any) { return err(e, 'Failed to change the trunk'); }
  },

  /** What a push of this limit would send to each switch, and what it withholds. Writes nothing. */
  pushPreview: async (limitId: number): Promise<ApiResponse<PushPreview>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.CAPACITY_PUSH_PREVIEW(limitId), {})).data; }
    catch (e: any) { return err(e, 'Failed to preview the push'); }
  },

  /** Master: push this limit to its switch(es) and read it back. Refused while voiceflow.sync.enabled=false. */
  push: async (limitId: number): Promise<ApiResponse<PushOutcome>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.CAPACITY_PUSH(limitId), {})).data; }
    catch (e: any) { return err(e, 'Failed to push to the switch'); }
  },

  /** The stored sync status of one limit (capacity_limit_sync). */
  limitSync: async (limitId: number): Promise<ApiResponse<LimitSyncRow[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.CAPACITY_LIMIT_SYNC(limitId))).data; }
    catch (e: any) { return err(e, 'Failed to load sync status'); }
  },

  resolve: async (params: { number?: string; trunk?: string; businessId?: string; campaignId?: string }): Promise<ApiResponse<ResolveResult>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.CAPACITY_LIMITS_RESOLVE, { params })).data; }
    catch (e: any) { return err(e, 'Failed to resolve'); }
  }
};
